import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  VendorDetailResponse,
  VendorMutationResponse,
  VendorPayload,
  VendorsFilter,
  VendorsResponse,
} from "../types/vendors.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.vendors;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const vendorsService = {
  async list(filter: VendorsFilter): Promise<VendorsResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<VendorsResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load vendors due to an unexpected error.",
      ) as unknown as VendorsResponse;
    }
  },

  async details(id: number | string): Promise<VendorDetailResponse> {
    try {
      return await apiClient<VendorDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load vendor details due to an unexpected error.",
      ) as unknown as VendorDetailResponse;
    }
  },

  async create(payload: VendorPayload): Promise<VendorMutationResponse> {
    try {
      return await apiClient<VendorMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create vendor due to an unexpected error.",
      ) as VendorMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<VendorPayload>,
  ): Promise<VendorMutationResponse> {
    try {
      return await apiClient<VendorMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update vendor due to an unexpected error.",
      ) as VendorMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<VendorMutationResponse> {
    return vendorsService.update(id, payload);
  },

  async delete(id: number | string): Promise<VendorMutationResponse> {
    try {
      return await apiClient<VendorMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete vendor due to an unexpected error.",
      ) as VendorMutationResponse;
    }
  },
};
